import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import "./App.css";

// Import Firebase config
import "./firebase/config";

// Import screens
import LandingPage from "./components/LandingPage";
import WebAuthGate from "./components/WebAuthGate";
import { getSignInRedirectPath } from "./config/webAuth";
import HomeScreen from "./screens/HomeScreen";
import HomeScreenLoggedIn from "./screens/HomeScreenLoggedIn";
import LoginScreen from "./screens/LoginScreen";
import RegisterScreen from "./screens/RegisterScreen";
import MarketScreen from "./screens/MarketScreen";
import InvestScreen from "./screens/InvestScreen";
import AccountScreen from "./screens/AccountScreen";
import AnalizeScreen from "./screens/AnalizeScreen";
import StockDetailScreen from "./screens/StockDetailScreen";
import AddInvestmentScreen from "./screens/AddInvestmentScreen";
import AddTransactionScreen from "./screens/AddTransactionScreen";
import TransactionHistoryScreen from "./screens/TransactionHistoryScreen";
import SetupCashAvailableScreen from "./screens/SetupCashAvailableScreen";
import AdminPanelScreen from "./screens/AdminPanelScreen";
import AIVoiceTestScreen from "./screens/AIVoiceTestScreen";
import CurrencyDetailScreen from "./screens/CurrencyDetailScreen";
import MarketScreenLoggedIn from "./screens/MarketScreenLoggedIn";
import InvestScreenLoggedIn from "./screens/InvestScreenLoggedIn";
import AnalizeScreenLoggedIn from "./screens/AnalizeScreenLoggedIn";
import AccountScreenLoggedIn from "./screens/AccountScreenLoggedIn";
import DownloadScreen from "./screens/DownloadScreen";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsAndConditionsPage from "./pages/TermsAndConditionsPage";
import SupportPage from "./pages/SupportPage";
import DeleteAccountPage from "./pages/DeleteAccountPage";
import TransactionImportPage from "./pages/TransactionImportPage";
import TestScreen from "./screens/TestScreen";

// Import Navigation
import Navigation from "./components/Navigation";

function AppContent() {
  const { user, loading } = useAuth();
  const signInPath = getSignInRedirectPath();

  if (loading) {
    return (
      <div className="screen">
        <div className="card">
          <h2>Loading...</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <Routes>
        <Route
          path="/"
          element={
            <LandingPage user={user}>
              {!user ? null : <HomeScreenLoggedIn />}
            </LandingPage>
          }
        />
        <Route
          path="/market"
          element={
            <LandingPage user={user}>
              {user ? <MarketScreenLoggedIn /> : <MarketScreen user={user} />}
            </LandingPage>
          }
        />
        <Route
          path="/analize"
          element={
            <LandingPage user={user}>
              {user ? <AnalizeScreenLoggedIn /> : <AnalizeScreen user={user} />}
            </LandingPage>
          }
        />
        {user && (
          <>
            <Route
              path="/invest"
              element={
                <LandingPage user={user}>
                  <InvestScreenLoggedIn />
                </LandingPage>
              }
            />
            <Route
              path="/account"
              element={
                <LandingPage user={user}>
                  <AccountScreenLoggedIn />
                </LandingPage>
              }
            />
          </>
        )}
        <Route
          path="/login"
          element={
            <WebAuthGate>
              <LoginScreen />
            </WebAuthGate>
          }
        />
        <Route
          path="/register"
          element={
            <WebAuthGate>
              <RegisterScreen />
            </WebAuthGate>
          }
        />
        <Route path="/stock-detail/:symbol" element={<StockDetailScreen />} />
        <Route path="/add-investment" element={<AddInvestmentScreen />} />
        <Route path="/add-transaction" element={<AddTransactionScreen />} />
        <Route
          path="/transaction-history"
          element={<TransactionHistoryScreen />}
        />
        <Route path="/setup-cash" element={<SetupCashAvailableScreen />} />
        <Route path="/admin-panel" element={<AdminPanelScreen />} />
        <Route path="/ai-voice-test" element={<AIVoiceTestScreen />} />
        <Route
          path="/currency-detail/:symbol"
          element={<CurrencyDetailScreen />}
        />
        <Route
          path="/test"
          element={
            user ? (
              <LandingPage user={user}>
                <TestScreen />
              </LandingPage>
            ) : (
              <Navigate to={signInPath} replace />
            )
          }
        />
        <Route
          path="/download"
          element={
            !user ? (
              <LandingPage user={user}>
                <DownloadScreen />
              </LandingPage>
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route
          path="/privacy"
          element={
            <LandingPage user={user}>
              <PrivacyPolicyPage />
            </LandingPage>
          }
        />
        <Route
          path="/terms"
          element={
            <LandingPage user={user}>
              <TermsAndConditionsPage />
            </LandingPage>
          }
        />
        <Route
          path="/support"
          element={
            <LandingPage user={user}>
              <SupportPage />
            </LandingPage>
          }
        />
        <Route
          path="/delete-account"
          element={
            <LandingPage user={user}>
              <DeleteAccountPage />
            </LandingPage>
          }
        />
        <Route
          path="/import-transactions"
          element={
            user ? (
              <LandingPage user={user}>
                <TransactionImportPage />
              </LandingPage>
            ) : (
              <Navigate to={signInPath} replace />
            )
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppContent />
      </Router>
    </AuthProvider>
  );
}

export default App;
